import React from 'react';
import { Box, Button, Typography, Container, Paper } from '@mui/material';
import { Refresh, Home, Agriculture, Update } from '@mui/icons-material';
import wiltingPlant from '../static_assets/wilting_plant.gif';
import loaderShoot from '../static_assets/loader_shoot.gif';

/**
 * Component to handle chunk loading errors (lazy loading failures)
 * This prevents white screens when code splitting fails
 */
class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false
    };
  }

  static getDerivedStateFromError(error) {
    // Check if it's a chunk loading error
    const isChunkError = 
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module');
    
    return { 
      hasError: true,
      isChunkError
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('=== CHUNK LOAD ERROR ===');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('========================');
    
    this.setState({
      error,
      errorInfo
    });

    // Store error for debugging
    try {
      localStorage.setItem('lastChunkError', JSON.stringify({
        message: error?.message,
        name: error?.name,
        timestamp: new Date().toISOString(),
      }));
    } catch (e) {
      console.error('Failed to store chunk error:', e);
    }
  }

  handleReload = () => {
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload(true);
  };

  handleReset = () => {
    this.setState({ 
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // Special UI for chunk loading errors - FarmSmarter themed
        return (
          <Box
            sx={{
              minHeight: '100vh',
              background: 'linear-gradient(180deg, #0E8BEF 20.31%, #718A33 81.77%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 2,
            }}
          >
            <Container maxWidth="md">
              <Paper
                elevation={8}
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  bgcolor: 'rgba(255, 255, 255, 0.98)',
                }}
              >
                {/* Header Section */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #003C37 0%, #14504B 100%)',
                    padding: 4,
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Decorative Background Pattern */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      opacity: 0.1,
                      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)',
                    }}
                  />

                  {/* Icon */}
                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      mb: 2,
                    }}
                  >
                    <img
                      src={loaderShoot}
                      alt="Update"
                      style={{
                        width: '120px',
                        height: '120px',
                        filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      color: '#D5EE97',
                      fontWeight: 700,
                      mb: 1,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    Fresh Updates Available!
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      color: '#A0DCD7',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    New features are ready to harvest
                  </Typography>
                </Box>

                {/* Content Section */}
                <Box sx={{ padding: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#003C37',
                      mb: 3,
                      textAlign: 'center',
                      fontSize: '1.1rem',
                    }}
                  >
                    A new version of FarmSmarter is available! Reload the page to enjoy the latest improvements and features.
                  </Typography>

                  {/* Action Button */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Refresh />}
                      onClick={this.handleReload}
                      sx={{
                        bgcolor: '#003C37',
                        color: '#D5EE97',
                        fontWeight: 600,
                        px: 5,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        '&:hover': {
                          bgcolor: '#14504B',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,60,55,0.3)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Reload & Update
                    </Button>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      display: 'block',
                      textAlign: 'center',
                    }}
                  >
                    This will clear your cache and load the latest version
                  </Typography>

                  {/* Info Box */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: '#E8F5E9',
                      borderRadius: 2,
                      textAlign: 'center',
                    }}
                  >
                    <Update sx={{ color: '#4CAF50', fontSize: 32, mb: 1 }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#2E7D32',
                        fontWeight: 500,
                      }}
                    >
                      We're constantly improving FarmSmarter to help you grow better!
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Container>
          </Box>
        );
      }

      // Generic error UI - FarmSmarter themed
      return (
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #0E8BEF 20.31%, #718A33 81.77%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 2,
          }}
        >
          <Container maxWidth="md">
            <Paper
              elevation={8}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'rgba(255, 255, 255, 0.98)',
              }}
            >
              {/* Header Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #003C37 0%, #14504B 100%)',
                  padding: 4,
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative Background Pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)',
                  }}
                />

                {/* Icon */}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    mb: 2,
                  }}
                >
                  <img
                    src={wiltingPlant}
                    alt="Error"
                    style={{
                      width: '120px',
                      height: '120px',
                      filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))',
                    }}
                  />
                </Box>

                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    color: '#D5EE97',
                    fontWeight: 700,
                    mb: 1,
                    position: 'relative',
                    zIndex: 1,
                    textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                  }}
                >
                  Oops! Something Went Wrong
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    color: '#A0DCD7',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  We've hit a snag in the field
                </Typography>
              </Box>

              {/* Content Section */}
              <Box sx={{ padding: 4 }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#003C37',
                    mb: 3,
                    textAlign: 'center',
                    fontSize: '1.1rem',
                  }}
                >
                  Don't worry! Just like a crop that needs a little care, we can get things growing again.
                </Typography>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Refresh />}
                    onClick={this.handleReset}
                    sx={{
                      bgcolor: '#003C37',
                      color: '#D5EE97',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        bgcolor: '#14504B',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,60,55,0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Try Again
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Home />}
                    onClick={this.handleReload}
                    sx={{
                      borderColor: '#003C37',
                      color: '#003C37',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      borderWidth: 2,
                      '&:hover': {
                        borderColor: '#14504B',
                        bgcolor: 'rgba(0,60,55,0.05)',
                        borderWidth: 2,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Go Home
                  </Button>
                </Box>

                {/* Support Message */}
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: '#E8F5E9',
                    borderRadius: 2,
                    textAlign: 'center',
                  }}
                >
                  <Agriculture sx={{ color: '#4CAF50', fontSize: 32, mb: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#2E7D32',
                      fontWeight: 500,
                    }}
                  >
                    If the problem persists, our support team is here to help you cultivate success!
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;

